import React, { useEffect, useState } from "react";
import { Card, CardBody, Container, Modal, ModalBody, Spinner } from "reactstrap";
import {
  PERMISSION_MODULES,
  PERMISSION_ACTIONS,
} from "../../../../common/constants/PermissionConstants";
import AddStudyPage from "./AddStudyForm";
import { useNavigate, useLocation } from "react-router-dom";
import tableStyles from "../../../../assets/scss/common/table.module.scss";
import styles from "../css/masterstudylist.module.scss";
import { useStudyDetailsService } from "../../../../hooks/useCommonQuery";
import { useStageSelectWorkflowData } from "../../../Library/Workflow/api/useWorkflowQuery";

import StudyFilterModal from "../components/StudyFilterModal";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import CommonButton from "../../../../Components/Common/CommonButton";
import { normalizeFormValues, studyListColumn } from "../helpers/studyconstant";
import { Study, StudyFilterFormValues, StudyFormValues } from "../helpers/study.types";
import {
  GetUserData,
  perPageData,
  showToastMessage,
} from "common/service/CommonService";
import CommonSearch from "Components/Common/CommonSearch";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import FilePreviewModal from "modules/Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";

import PinVerificationModal from "../../../Pharmacy/DailyReconsilation/components/PinVerificationModal";
import { usePermission } from "context/PermissionContext";
import { loadCodelists, loadSponsors, loadStudyDesign } from "api/modules/common.option";

const StudyList: React.FC = () => {
  document.title = "Study Management | ZENTIXS VISTA";

  const navigate = useNavigate();
  const location = useLocation();

  //-------------------------------------------------------------------------------------------
  //-->  State
  //-------------------------------------------------------------------------------------------
  const initialTab = (location.state as any)?.activeTab || "ACTIVE";
  const [activeTab, setActiveTab] = useState<"ACTIVE" | "DRAFT" | "COMPLETED" | "ARCHIVE">(initialTab);

  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [isDraft, setIsDraft] = useState<boolean>(initialTab === "DRAFT");
  const [studyStatus, setStudyStatus] = useState<string | null>(initialTab === "DRAFT" ? "Draft" : "Active");
  const [filterCriteria, setFilterCriteria] =
    useState<StudyFilterFormValues | null>(null);
  const [isFilterApplied, setIsFilterApplied] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const userData = GetUserData();

  //Audit
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [loadingStudyId, setLoadingStudyId] = useState<number | null>(null);
  const [archivingStudyId, setArchivingStudyId] = useState<number | null>(null);
  const [completingStudyId, setCompletingStudyId] = useState<number | null>(null);
  const [verifyModalOpen, setVerifyModalOpen] = useState<boolean>(false);
  const [verifyModalMode, setVerifyModalMode] = useState<"COMPLETE" | "ARCHIVE" | null>(null);
  const [selectedStudyForVerify, setSelectedStudyForVerify] = useState<any | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const { selectedStudy: globalstudy } = useGlobalStudy();

  //-------------------------------------------------------------------------------------------
  //-->  Service Hooks
  //-------------------------------------------------------------------------------------------
  const { SelectAllQuery, UpdateMutation } = useStudyDetailsService(
    searchText,
    isDraft,
    undefined,
    filterCriteria,
    currentPageNumber,
    perPageData,
    studyStatus,
  );

  const { switchStageMutation } = useStageSelectWorkflowData(null);

  useEffect(() => {
    const load = async () => {
      const result = await loadCodelists([
        "Country_OPTIONS",
        "Routeof_Administration_OPTIONS",
        "Light_Condition_OPTIONS",
        "Gender_OPTIONS",
        "Biological_matrix_OPTIONS",
        "Fasting_Condition_OPTIONS",
        "StudyType_OPTIONS",
        "SEQUENCE_NUMBER_OPTIONS",
        "AMBULATORY_VISIT_TYPE_OPTIONS",
        "AMBULATORY_VISIT_CATEGORY_OPTIONS",
        "AMBULATORY_VISIT_ACTIVITY_CATEGORY_OPTIONS",
        "Checklist_Type_OPTIONS",
        "CHECKLIST_CATEGORY_OPTIONS",
      ]);
      const allLoaded = Object.values(result).every((flag) => flag);
      if (!allLoaded) {
        showToastMessage("error", "Some dropdowns failed to load");
      }
    };
    load();
    loadSponsors();
    loadStudyDesign();
  }, []);

  const {
    data: queryResponse,
    isLoading,
    isFetching,
  } = SelectAllQuery as {
    data?: any;
    isLoading: boolean;
    isError: boolean;
    isFetching: boolean;
  };

  const allData: Study[] = Array.isArray(queryResponse)
    ? (queryResponse as Study[])
    : ((queryResponse as any)?.data as Study[]) ?? [];

  const displayTotal = allData.length > 0 ? allData[0]?.total_count : 0;

  //-------------------------------------------------------------------------------------------
  //-->  Handlers
  //-------------------------------------------------------------------------------------------
  const toggleModal = (): void => setModalOpen(!modalOpen);
  const toggleFilterModal = (): void => setFilterOpen(!filterOpen);

  const handleTabChange = (tab: "ACTIVE" | "DRAFT" | "COMPLETED" | "ARCHIVE") => {
    setActiveTab(tab);
    if (tab === "ACTIVE") {
      setIsDraft(false);
      setStudyStatus("Active");
    } else if (tab === "DRAFT") {
      setIsDraft(true);
      setStudyStatus("Draft");
    } else if (tab === "COMPLETED") {
      setIsDraft(false);
      setStudyStatus("Completed");
    } else if (tab === "ARCHIVE") {
      setIsDraft(false);
      setStudyStatus("Archived");
    }
    setCurrentPageNumber(1);
  };

  useEffect(() => {
    if (location.state && (location.state as any).activeTab) {
      handleTabChange((location.state as any).activeTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);

  const onSearch = (text: string | null) => {
    const next = text ?? "";
    setSearchText(next);
    setCurrentPageNumber(1);
    if (next !== "") {
      clearFilters();
    }
  };

  const onAdd = (): void => {
    toggleModal();
  };

  const handleFirstStageTransition = (study: any) => {
    const ws = study.workflowstage;
    if (!ws) return;

    const payload: any = {
      studyid: study.studyid,
      workflowid: study.workflowid,
      updatedby: userData?.userid,
      currentstageid: ws.currentstageid,
      nextstage: ws.nextstage,
      nextactionlabel: ws.nextactionlabel,
      nextstagestatus: ws.nextstagestatus,
      returnstage: ws.returnstage,
      returnactionlabel: ws.returnactionlabel,
      returnstagestatus: ws.returnstagestatus,
      islocked: ws.islocked,
      isForward: true,
      checklist: [],
    };

    switchStageMutation.mutate(payload, {
      onSuccess: (response: any) => {
        showToastMessage(
          "success", response?.message || "Study workflow review started successfully",
        );
        SelectAllQuery.refetch();
        navigate(`/qaqc/review/${study.studyid}`, {
          state: { workflowid: study.workflowid },
        });
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || (typeof err === "string" ? err : "Failed to transition study"));
      },
    });
  };

  const onFilter = (values: StudyFilterFormValues): void => {
    const normalizedValues = normalizeFormValues(values);
    const isAllEmpty = Object.values(normalizedValues).every(
      (value) => value === null || value === "" || value === undefined,
    );
    if (isAllEmpty) {
      setFilterCriteria(normalizedValues);
      setIsFilterApplied(false);
      return;
    }
    setSearchText("");
    setFilterCriteria(normalizedValues);
    setIsFilterApplied(true);
  };

  const clearFilters = () => {
    setFilterCriteria(null);
    setIsFilterApplied(false);
  };

  //--------------------------------------------------------
  // AUDIT
  //--------------------------------------------------------
  const onAuditClick = (StudyId: number) => {
    setLoadingStudyId(StudyId);
    const payload = {
      modulename: "Study",
      studyid: StudyId,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Study_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
        setLoadingStudyId(null);
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message ?? error);
        console.error("Error fetching audit logs:", error);
        setLoadingStudyId(null);
      },
    });
  };
  const handleArchiveStudy = (study: any) => {
    setSelectedStudyForVerify(study);
    setVerifyModalMode("ARCHIVE");
    setVerifyModalOpen(true);
  };

  const handleCompleteStudy = (study: any) => {
    setSelectedStudyForVerify(study);
    setVerifyModalMode("COMPLETE");
    setVerifyModalOpen(true);
  };


  const COMMON_UPDATE_FIELDS = ["studyid", "updatedby", "createdby", "userid"];

  const getChangedFields = (currentValues: any, originalValues: any) => {
    const changedFields: Record<string, any> = {};

    Object.keys(currentValues || {}).forEach((key) => {
      const currentValue = currentValues[key];
      const originalValue = originalValues?.[key];

      const hasChanged =
        JSON.stringify(currentValue) !== JSON.stringify(originalValue);

      if (hasChanged) {
        changedFields[key] = currentValue;
      }
    });

    return changedFields;
  };

  const getStudyUpdatePayload = (study: any, updatedValues: Record<string, any>) => {
    const normalizedCurrentValues = normalizeFormValues({
      ...study,
      ...updatedValues,
    });
    const normalizedOriginalValues = normalizeFormValues(study || {});
    const changedFields = getChangedFields(
      normalizedCurrentValues,
      normalizedOriginalValues,
    );

    const commonFields = COMMON_UPDATE_FIELDS.reduce((acc, key) => {
      if (normalizedOriginalValues?.[key] !== undefined) {
        acc[key] = normalizedOriginalValues[key];
      }
      return acc;
    }, {} as Record<string, any>);

    return { ...commonFields, ...changedFields };
  };

  const handlePinVerify = (password: string) => {
    if (!password || password.trim() === "") {
      return;
    }

    setIsVerifying(true);

    setTimeout(() => {
      setIsVerifying(false);
      setVerifyModalOpen(false);

      if (verifyModalMode === "COMPLETE") {
        setCompletingStudyId(selectedStudyForVerify.studyid);
        const payload = {
          ...getStudyUpdatePayload(selectedStudyForVerify, {
            isdraft: "completed",
          }),
          updatereason: "Study completed",
          verifypin: password,
          verifiedby: userData?.userid,
        } as Partial<StudyFormValues>;

        UpdateMutation.mutate(payload as StudyFormValues, {
          onSuccess: (response: any) => {
            showToastMessage("success", response?.message || "Study marked as completed successfully");
            setCompletingStudyId(null);
            setSelectedStudyForVerify(null);
            setVerifyModalMode(null);
            SelectAllQuery.refetch();
          },
          onError: (err: any) => {
            showToastMessage("error", err?.message || (typeof err === "string" ? err : "Failed to complete study"));
            setCompletingStudyId(null);
            setSelectedStudyForVerify(null);
            setVerifyModalMode(null);
          },
        });
      } else if (verifyModalMode === "ARCHIVE") {
        setArchivingStudyId(selectedStudyForVerify.studyid);
        const payload = {
          ...getStudyUpdatePayload(selectedStudyForVerify, {
            isdraft: "archive",
          }),
          updatereason: "Study archived",
          verifypin: password,
          verifiedby: userData?.userid,
        } as Partial<StudyFormValues>;

        UpdateMutation.mutate(payload as StudyFormValues, {
          onSuccess: (response: any) => {
            showToastMessage("success", response?.message || "Study archived successfully");
            setArchivingStudyId(null);
            setSelectedStudyForVerify(null);
            setVerifyModalMode(null);
            SelectAllQuery.refetch();
          },
          onError: (err: any) => {
            showToastMessage("error", err?.message || (typeof err === "string" ? err : "Failed to archive study"));
            setArchivingStudyId(null);
            setSelectedStudyForVerify(null);
            setVerifyModalMode(null);
          },
        });
      }
    }, 600);
  };
  const { hasPermission } = usePermission()
  //-------------------------------------------------------------------------------------------
  //-->  Columns Construction Based on Role/Tab
  //-------------------------------------------------------------------------------------------

  const activeColumns: any = [
    ...studyListColumn(navigate),
    ...(hasPermission(PERMISSION_MODULES.STUDY, PERMISSION_ACTIONS.COMPLETE_STUDY)
      ? [
          {
            key: "Complete",
            label: "Complete",
            textCenter: true,
            cell: (row: any) => {
              const isCurrentLoading = completingStudyId === row.studyid && UpdateMutation.isPending;
              return (
                <CommonButton
                  module={PERMISSION_MODULES.STUDY}
                  action={PERMISSION_ACTIONS.COMPLETE_STUDY}
                  color="success"
                  size="sm"
                  onClick={() => handleCompleteStudy(row)}
                  disabled={isCurrentLoading}
                  outline
                  className="rounded-pill px-3"
                  style={{ height: "26px" }}
                >
                  {isCurrentLoading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-checkbox-circle-line me-1"></i>
                  )}
                  Complete
                </CommonButton>
              );
            },
          },
        ]
      : []),
    ...(hasPermission(PERMISSION_MODULES.STUDY, PERMISSION_ACTIONS.AUDIT)
      ? [
          {
            key: "Audit",
            label: "Audit",
            textCenter: true,
            cell: (row: any) => {
              const isCurrentLoading = loadingStudyId === row.studyid && AuditMutation.isPending;
              return (
                <CommonButton
                  color="primary"
                  size="sm"
                  onClick={() => onAuditClick(row.studyid)}
                  disabled={isCurrentLoading}
                  outline
                  style={{ height: "25px" }}
                >
                  {isCurrentLoading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              );
            },
          },
        ]
      : []),
  ];

  const completedColumns: any = [
    ...studyListColumn(navigate),
    ...(hasPermission(PERMISSION_MODULES.STUDY, PERMISSION_ACTIONS.ARCHIVE_STUDY)
      ? [
          {
            key: "Archive",
            label: "Archive",
            textCenter: true,
            cell: (row: any) => {
              const isCurrentLoading = archivingStudyId === row.studyid && UpdateMutation.isPending;
              return (
                <CommonButton
                  color="warning"
                  size="sm"
                  onClick={() => handleArchiveStudy(row)}
                  disabled={isCurrentLoading}
                  outline
                  className="rounded-pill px-3"
                  style={{ height: "26px" }}
                >
                  {isCurrentLoading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-archive-line me-1"></i>
                  )}
                  Archive
                </CommonButton>
              );
            },
          },
        ]
      : []),
    ...(hasPermission(PERMISSION_MODULES.STUDY, PERMISSION_ACTIONS.AUDIT)
      ? [
          {
            key: "Audit",
            label: "Audit",
            textCenter: true,
            cell: (row: any) => {
              const isCurrentLoading = loadingStudyId === row.studyid && AuditMutation.isPending;
              return (
                <CommonButton
                  color="primary"
                  size="sm"
                  onClick={() => onAuditClick(row.studyid)}
                  disabled={isCurrentLoading}
                  outline
                  style={{ height: "25px" }}
                >
                  {isCurrentLoading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              );
            },
          },
        ]
      : []),
  ];

  const archiveColumns: any = [
    ...studyListColumn(navigate),
    ...(hasPermission(PERMISSION_MODULES.STUDY, PERMISSION_ACTIONS.ARCHIVE_STUDY)
      ? [
          {
            key: "Audit",
            label: "Audit",
            textCenter: true,
            cell: (row: any) => {
              const isCurrentLoading = loadingStudyId === row.studyid && AuditMutation.isPending;
              return (
                <CommonButton
                  color="primary"
                  size="sm"
                  onClick={() => onAuditClick(row.studyid)}
                  disabled={isCurrentLoading}
                  outline
                  style={{ height: "25px" }}
                >
                  {isCurrentLoading ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
              );
            },
          },
        ]
      : []),
  ];

  const draftColumns: any = [
    ...studyListColumn(navigate),
    {
      key: "actions",
      label: "Actions",
      textCenter: true,
      cell: (row: any) => {
        const currentUserRoleId = Number(userData?.roleid);
        const isRoleMatched = row.isfirststage
          ? currentUserRoleId === Number(row.workflowstage?.nextstagerole)
          : true;

        return (
          <button
            className={styles.reviewBtn}
            disabled={!isRoleMatched}
            style={{
              opacity: isRoleMatched ? 1 : 0.6,
              cursor: isRoleMatched ? "pointer" : "not-allowed",
              filter: isRoleMatched ? "none" : "grayscale(0.5)",
            }}
            onClick={() => {
              if (row.isfirststage) {
                handleFirstStageTransition(row);
              } else {
                navigate(`/qaqc/review/${row.studyid || row.id}`, {
                  state: { workflowid: row.workflowid },
                });
              }
            }}
            title={
              isRoleMatched
                ? "Start Review"
                : "You do not have permission for this stage"
            }
          >
            <i className="ri-file-search-line" />
            {row.isfirststage
              ? row.workflowstage?.currentstagename
              : row.workflowstage?.currentstagestatus}
          </button>
        );
      },
    },
  ];

  const activeGridColumns =
    activeTab === "DRAFT"
      ? draftColumns
      : activeTab === "COMPLETED"
        ? completedColumns
        : activeTab === "ARCHIVE"
          ? archiveColumns
          : activeColumns;

  //-------------------------------------------------------------------------------------------
  //-->  Render
  //-------------------------------------------------------------------------------------------
  return (
    <div className="page-content">
      <Container fluid>
        <div>
          <Card className={`${tableStyles.card} mb-0`}>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center">
                {/* Tab Toggles */}
                <div
                  className="d-inline-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: "#f3f6f9",
                    borderRadius: "50px",
                    padding: "4px",
                    border: "1px solid #e9ebec",
                  }}
                >
                  <button
                    className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${activeTab === "ACTIVE" ? "text-primary shadow-sm" : "text-muted"}`}
                    style={{
                      backgroundColor:
                        activeTab === "ACTIVE" ? "#ffffff" : "transparent",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => handleTabChange("ACTIVE")}
                  >
                    <i className={`ri-list-check me-1`} /> Active Studies
                  </button>
                  <button
                    className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${activeTab === "DRAFT" ? "text-primary shadow-sm" : "text-muted"}`}
                    style={{
                      backgroundColor:
                        activeTab === "DRAFT" ? "#ffffff" : "transparent",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => handleTabChange("DRAFT")}
                  >
                    <i className={`ri-draft-line me-1`} /> Draft Studies
                  </button>
                  <button
                    className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${activeTab === "COMPLETED" ? "text-primary shadow-sm" : "text-muted"}`}
                    style={{
                      backgroundColor:
                        activeTab === "COMPLETED" ? "#ffffff" : "transparent",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => handleTabChange("COMPLETED")}
                  >
                    <i className={`ri-checkbox-circle-line me-1`} /> Completed Studies
                  </button>
                  <button
                    className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${activeTab === "ARCHIVE" ? "text-primary shadow-sm" : "text-muted"}`}
                    style={{
                      backgroundColor:
                        activeTab === "ARCHIVE" ? "#ffffff" : "transparent",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => handleTabChange("ARCHIVE")}
                  >
                    <i className={`ri-archive-line me-1`} /> Archived Studies
                  </button>
                </div>

                {/* Search & Filter */}
                <div className={sf.wrapper}>
                  <CommonSearch onSearch={onSearch} searchText={searchText} />
                  <button
                    className={`${sf.filterBtn} ${isFilterApplied ? sf.active : ""}`}
                    onClick={toggleFilterModal}
                    title="Filter Studies"
                  >
                    <i className="ri-filter-3-line" style={{ fontSize: 15 }} />
                    {isFilterApplied && <span className={sf.filterDot} />}
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="d-flex align-items-center gap-2">
                  <CommonButton
                    color="primary"
                    size="sm"
                    onClick={onAdd}
                    outline
                    module={PERMISSION_MODULES.STUDY}
                    action={PERMISSION_ACTIONS.ADD}
                  >
                    <i className="ri-add-line"></i> Add
                  </CommonButton>
                </div>
              </div>

              {/* Table */}
              <div className="mt-3">
                <CommonTableIndexWise
                  columns={activeGridColumns}
                  data={allData}
                  moduleName={PERMISSION_MODULES.STUDY}
                  className={tableStyles.studyList}
                  hideAction={true}
                  showStatus={false}
                  pageSize={perPageData}
                  currentPage={currentPageNumber}
                  onPageChange={setCurrentPageNumber}
                  totalRecords={displayTotal}
                  loading={isLoading || isFetching}
                />
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Add / Edit Modal */}
        <Modal
          isOpen={modalOpen}
          toggle={toggleModal}
          centered
          size="xl"
          backdrop="static"
          fullscreen
        >
          <ModalBody className="p-0">
            <AddStudyPage
              onClose={toggleModal}
              onCreated={() => {
                handleTabChange("DRAFT");
                setSearchText("");
                setFilterCriteria(null);
                setIsFilterApplied(false);
                setCurrentPageNumber(1);
              }}
            />
          </ModalBody>
        </Modal>

        {/* Filter Modal */}
        <StudyFilterModal
          isOpen={filterOpen}
          toggle={toggleFilterModal}
          onApply={onFilter}
          onClear={clearFilters}
          existingFilters={filterCriteria}
        />

        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}

        <PinVerificationModal
          isOpen={verifyModalOpen}
          toggle={() => setVerifyModalOpen(false)}
          onVerify={handlePinVerify}
          isLoading={isVerifying}
          title={verifyModalMode === "COMPLETE" ? "Verify Study Completion" : "Verify Study Archival"}
          description={
            verifyModalMode === "COMPLETE" ? (
              <>
                Please enter your password to mark Study Code: <strong>{selectedStudyForVerify?.studycode}</strong> as Completed.
              </>
            ) : (
              <>
                Please enter your password to archive Study Code: <strong>{selectedStudyForVerify?.studycode}</strong>.
              </>
            )
          }
        />
      </Container>
    </div>
  );
};

export default StudyList;
